import { Box, SxProps, Theme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { PortValuesHeatPump } from '../types/ports'
import {
  heatPumpActiveFlags, heatPumpCop, heatPumpFault, heatPumpHasProblem, heatPumpHeat, heatPumpModeKey, heatPumpStatusKey,
  heatPumpTarget, heatPumpTemperatures
} from '../classes/ports/heat-pump-status'

export interface HeatPumpStatusViewProps {
  values?: Partial<PortValuesHeatPump>
  sx?: SxProps<Theme>
}

function kW(watts: number | null | undefined): string {
  return typeof watts === 'number' ? `${(watts / 1000).toFixed(2)} kW` : '–'
}

/**
 * Live status of a heat pump (port 0x23): link status, mode, powers, COP,
 * compressor, temperatures and the key flags. What is left out on purpose is
 * listed in heat-pump-status.ts.
 */
export function HeatPumpStatusView(props: HeatPumpStatusViewProps): React.ReactElement {
  const { values, sx } = props
  const { t } = useTranslation()

  const statusKey = heatPumpStatusKey(values)
  if (!statusKey) {
    return <Box sx={sx} data-cy="box-Heat pump status">{t('HeatPump-No data yet')}</Box>
  }

  const problem = heatPumpHasProblem(values)
  const fault = heatPumpFault(values)
  const modeKey = heatPumpModeKey(values)
  const cop = heatPumpCop(values)
  const temperatures = heatPumpTemperatures(values)
  const flags = heatPumpActiveFlags(values)
  const hz = values?.compressor_hz
  const target = heatPumpTarget(values)
  const auxPower = values?.aux_power || 0

  const cell = { display: 'flex', justifyContent: 'space-between', gap: 1 }

  return (
    <Box sx={sx} data-cy="box-Heat pump status">
      <Box sx={{ color: problem ? 'error.light' : 'inherit', mb: 1 }} data-cy="text-Heat pump link">
        {t(statusKey)}{fault && ` (${t('HeatPump-Fault code')} ${fault})`}
        {modeKey && !problem && <> &middot; {t(modeKey)}</>}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(11rem, 1fr))', columnGap: 3, rowGap: 0.5 }}>
        <Box sx={cell}><span>{t('HeatPump-Electrical power')}</span><b>{kW(values?.power)}</b></Box>
        <Box sx={cell}><span>{t('HeatPump-Thermal power')}</span><b>{kW(heatPumpHeat(values))}</b></Box>
        <Box sx={cell} data-cy="text-Heat pump COP"><span>COP</span><b>{cop === null ? '–' : cop.toFixed(1)}</b></Box>
        <Box sx={cell}><span>{t('HeatPump-Compressor')}</span><b>{typeof hz === 'number' ? `${hz} Hz` : '–'}</b></Box>
        {target &&
          <Box sx={cell} data-cy="text-Heat pump target">
            <span>{t('HeatPump-Target')}</span>
            <b>{'key' in target ? t(target.key) : `${kW(target.watts)}${target.dutyCycling ? ` (${t('HeatPump-duty cycling')})` : ''}`}</b>
          </Box>
        }
        {auxPower > 0 && <Box sx={cell}><span>{t('HeatPump-Electric backup heater')}</span><b>{kW(auxPower)}</b></Box>}
        {temperatures.map(([field, key, value]) =>
          <Box key={field} sx={cell} data-cy={`text-Heat pump ${field}`}><span>{t(key)}</span><b>{value.toFixed(1)} °C</b></Box>
        )}
      </Box>
      {flags.length > 0 &&
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {flags.map(key =>
            <Box key={key} component="span" sx={{ px: 1, border: '1px solid', borderColor: 'primary.main', borderRadius: '0.75rem', fontSize: '85%' }}>
              {t(key)}
            </Box>
          )}
        </Box>
      }
    </Box>
  )
}
